import { isPlatformBrowser } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  OnInit,
  PLATFORM_ID,
  ViewChild
} from "@angular/core";
import { AppActionTypes } from "@app/store/actions/app.actions";
import { FindImages, FindImagesSuccess } from "@app/store/actions/image.actions";
import { MainState } from "@app/store/state";
import { ImageAlias } from "@core/enums/image-alias.enum";
import { ImageInterface } from "@core/interfaces/image.interface";
import { ImageService } from "@core/services/image/image.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { WindowRefService } from "@core/services/window-ref.service";
import { NgbActiveOffcanvas } from "@ng-bootstrap/ng-bootstrap";
import { Actions, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { fadeInOut } from "@shared/animations";
import { BaseComponentDirective } from "@shared/components/base-component.directive";
import { fromEvent, Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, map, takeUntil, throttleTime } from "rxjs/operators";

@Component({
  selector: "astrobin-tiptap-gallery-image-selector",
  template: `
    <div class="offcanvas-header">
      <h5 class="offcanvas-title">{{ "Select image from your gallery" | translate }}</h5>
      <button (click)="activeOffcanvas.dismiss()" class="btn-close"></button>
    </div>
    <div class="offcanvas-body">
      <div class="gallery-selector-container">
        <div class="search-input-wrapper mb-3">
          <input
            #searchInput
            [ngModelOptions]="{ standalone: true }"
            (ngModelChange)="imagesSearchSubject.next($event)"
            class="form-control"
            [(ngModel)]="imagesSearch"
            placeholder="{{ 'Search by title' | translate }}"
            type="text"
          />
          <button
            *ngIf="imagesSearch"
            [attr.aria-label]="'Clear search' | translate"
            (click)="clearSearch()"
            class="clear-search-button"
            type="button"
          >
            <fa-icon icon="times"></fa-icon>
          </button>
        </div>

        <div class="mb-3">
          <astrobin-toggle-button
            [label]="'Include staging area' | translate"
            [value]="includeStagingArea"
            (toggle)="onIncludeStagingAreaChange($event)"
          ></astrobin-toggle-button>
        </div>

        <astrobin-loading-indicator *ngIf="loadingImages" @fadeInOut class="mt-2"></astrobin-loading-indicator>

        <div *ngIf="!searching && images?.length === 0 && !loadingImages" class="no-images-message text-center py-5">
          <p class="text-muted">{{ "You don't have any images yet." | translate }}</p>
        </div>

        <astrobin-masonry-layout
          *ngIf="!searching && images?.length > 0"
          [idProperty]="'pk'"
          [items]="images"
          layout="small"
        >
          <ng-template let-item>
            <div
              @fadeInOut
              [ngbTooltip]="item.title"
              (click)="selectImage(item)"
              class="image-container"
              container="body"
            >
              <img [alt]="item.title" [src]="imageService.getThumbnail(item, ImageAlias.GALLERY)" loading="lazy" />
            </div>
          </ng-template>
        </astrobin-masonry-layout>

        <astrobin-loading-indicator *ngIf="loadingMoreImages" @fadeInOut class="mt-2"></astrobin-loading-indicator>
      </div>
    </div>
  `,
  styleUrls: ["./tiptap-gallery-image-selector.component.scss"],
  animations: [fadeInOut],
  host: {
    style: "display: flex; flex-direction: column; height: 100%;"
  }
})
export class TiptapGalleryImageSelectorComponent extends BaseComponentDirective implements OnInit, AfterViewInit {
  @Input() userId: number;
  @ViewChild("searchInput") searchInput: ElementRef<HTMLInputElement>;

  protected readonly ImageAlias = ImageAlias;

  protected searching = true;
  protected imagesSearch: string;
  protected imagesSearchSubject = new Subject<string>();
  protected images: ImageInterface[] = [];
  protected loadingImages = false;
  protected loadingMoreImages = false;
  protected includeStagingArea = false;

  private readonly _isBrowser: boolean;
  private _page = 1;
  private _next: string | null = null;

  constructor(
    public readonly store$: Store<MainState>,
    public readonly actions$: Actions,
    public readonly activeOffcanvas: NgbActiveOffcanvas,
    public readonly elementRef: ElementRef,
    public readonly windowRefService: WindowRefService,
    @Inject(PLATFORM_ID) private platformId: Object,
    public readonly utilsService: UtilsService,
    public readonly imageService: ImageService,
    public readonly changeDetectorRef: ChangeDetectorRef,
    public readonly translateService: TranslateService
  ) {
    super(store$);
    this._isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this._setupOnScroll();

    this.imagesSearchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe(searchTerm => {
        this._searchImages(searchTerm);
        this.changeDetectorRef.markForCheck();
      });

    this.actions$
      .pipe(
        ofType(AppActionTypes.FIND_IMAGES_SUCCESS),
        map((action: FindImagesSuccess) => action.payload),
        takeUntil(this.destroyed$)
      )
      .subscribe(payload => {
        this.loadingImages = false;
        this.loadingMoreImages = false;
        this._next = payload.response.next;
        this.images = this.images.concat(payload.response.results);
        this.searching = false;
        this.changeDetectorRef.markForCheck();
      });

    this._searchImages(null);
    this.loadingImages = true;
  }

  ngAfterViewInit(): void {
    // Focus the search input after the view is initialized
    if (this._isBrowser && this.searchInput && this.searchInput.nativeElement) {
      // Use utility service for delay instead of setTimeout
      this.utilsService.delay(100).subscribe(() => {
        this.searchInput.nativeElement.focus();
      });
    }
  }

  protected selectImage(image: ImageInterface): void {
    this.activeOffcanvas.close(image);
  }

  protected clearSearch(): void {
    this.imagesSearch = "";
    this.imagesSearchSubject.next("");
  }

  protected onIncludeStagingAreaChange(value: boolean): void {
    this.includeStagingArea = value;
    this._searchImages(this.imagesSearch);
  }

  private _searchImages(searchTerm: string): void {
    this.loadingImages = true;
    this.searching = true;
    this.images = [];
    this._page = 1;
    this._next = null;

    this.store$.dispatch(
      new FindImages({
        options: {
          userId: this.userId,
          gallerySerializer: true,
          includeStagingArea: this.includeStagingArea,
          q: searchTerm,
          page: this._page
        }
      })
    );
  }

  private _setupOnScroll(): void {
    if (!this._isBrowser) {
      return;
    }

    const scrollableElement = UtilsService.getScrollableParent(this.elementRef.nativeElement, this.windowRefService);

    fromEvent(scrollableElement, "scroll")
      .pipe(throttleTime(250), takeUntil(this.destroyed$))
      .subscribe(() => {
        const isNearBottom = this.utilsService.isNearBottom(this.windowRefService, this.elementRef);

        if (isNearBottom && !this.loadingImages && !this.loadingMoreImages && this._next) {
          this.loadingMoreImages = true;
          this._page++;

          this.store$.dispatch(
            new FindImages({
              options: {
                userId: this.userId,
                gallerySerializer: true,
                includeStagingArea: this.includeStagingArea,
                q: this.imagesSearch,
                page: this._page
              }
            })
          );

          this.changeDetectorRef.markForCheck();
        }
      });
  }
}
